import React, { useState } from 'react';
import { Box, FormHelperText, LinearProgress, TextField } from '@mui/material';
import UserService from '../../services/UserService';
import { useSnackbar } from 'notistack';
import { UpdatePasswordByUserDto } from "@ontime/types";
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import CustomFormAction from '../../customComponents/CustomFormAction';
import CustomButton from '../../customComponents/CustomButton';

const userPasswordSchema = z.object({
    currentPassword: z.string().min(8, "Username must be at least 4 characters").max(20),
    newPassword: z.string()
        .min(8, "Password must be at least 8 characters")
        .regex(/^(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/,
            "Password must contain at least one uppercase letter, one number, and one special character"),
    confirmNewPassword: z.string(),

}).refine((data) => data.newPassword === data.confirmNewPassword, {
    path: ["ConfirmPassword"],
    message: "Passwords do not match",
});

const defaultValues: UpdatePasswordByUserDto = {
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: '',
};

const UserUpdatePassword: React.FC = () => {

    const { enqueueSnackbar } = useSnackbar();

    const [loading, setLoading] = useState(false);

    const { register, handleSubmit, formState: { errors }, watch, setValue, reset } = useForm<UpdatePasswordByUserDto>({
        resolver: zodResolver(userPasswordSchema),
        defaultValues: defaultValues
    });

    const handleUpdate = async (data: UpdatePasswordByUserDto) => {
        setLoading(true);

        try {
            const response = await UserService.UpdatePasswordByUser({ ...data });
            if (response.response.success) {
                enqueueSnackbar('Password updated successfully.', { variant: 'success' });
                reset(defaultValues);

            } else {
                enqueueSnackbar(response.response.error?.message, { variant: 'error' });
            }
        } catch (error) {
            console.error(error, { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box>
            {loading ?
                <LinearProgress color="primary" />
                :
                <form onSubmit={handleSubmit(handleUpdate)} noValidate>

                    <Box mt={2}>
                        <TextField
                            variant="outlined"
                            fullWidth
                            label="Current password"
                            InputLabelProps={{ shrink: true }}
                            type="password"
                            {...register('currentPassword')}
                            error={!!errors.currentPassword}
                            helperText={errors.currentPassword?.message}
                        />
                    </Box>

                    <Box mt={2}>
                        <TextField
                            variant="outlined"
                            fullWidth
                            label="New password"
                            InputLabelProps={{ shrink: true }}
                            type="password"
                            {...register('newPassword')}
                            error={!!errors.newPassword}
                            helperText={errors.newPassword?.message}
                        />
                    </Box>

                    <Box mt={2}>
                        <TextField
                            variant="outlined"
                            fullWidth
                            label="Confirm new password"
                            InputLabelProps={{ shrink: true }}
                            type="password"
                            {...register('confirmNewPassword')}
                            error={!!errors.confirmNewPassword}
                            helperText={errors.confirmNewPassword?.message}
                        />
                    </Box>

                    {watch('newPassword') !== watch('confirmNewPassword') && <FormHelperText error sx={{ ml: 2 }}>Inputed passwords are not match</FormHelperText>}
                    <CustomFormAction>
                        <CustomButton
                            lable='Update password'
                            type='submit'
                            disabled={loading}
                            infoContent={`Update new password`}
                        />
                    </CustomFormAction>

                </form>}
        </Box>
    );
};

export default UserUpdatePassword;